import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { ApiError } from "../sync/api";
import type { Artifact, ArtifactEntry, ArtifactVersion, UUID } from "../sync/types";
import { Badge, Card, EmptyState, Page } from "./ui";
import { locale, t, tn, tx } from "../i18n";

// ArtifactView shows a shared artifact (ADR-0006): a document with its
// versions, which people can edit (compare-and-set, so a newer version is
// never overwritten), or key-value state agents keep.
export function ArtifactView({ id }: { id: UUID }) {
  const session = useSession();
  const state = useLantern();
  const [a, setA] = useState<Artifact | null>(null);
  const [version, setVersion] = useState(0);
  const [versions, setVersions] = useState<ArtifactVersion[]>([]);
  const [entries, setEntries] = useState<ArtifactEntry[]>([]);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState("");
  const bump = a?.channel_id ? state.artifactBumps.get(a.channel_id) : undefined;

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { artifact } = await session.api.artifact(id, version);
        if (!live) return;
        setA(artifact);
        if (artifact.kind === "document") setVersions((await session.api.artifactVersions(id)).versions);
        else setEntries((await session.api.artifactEntries(id)).entries);
        setError("");
      } catch (err) {
        if (live) setError(err instanceof ApiError && err.status === 404 ? t("This artifact does not exist or you cannot see it.") : String(err));
      }
    })();
    return () => {
      live = false;
    };
  }, [session, id, version, bump]);

  const save = async () => {
    if (!a || draft === null) return;
    try {
      const { artifact } = await session.api.updateArtifact(id, draft, a.version);
      setA(artifact);
      setDraft(null);
      setVersions((await session.api.artifactVersions(id)).versions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  if (!a)
    return (
      <Page className="artifact-view" title={t("Shared artifact")} label={t("Artifact")}>
        {error ? <p className="error">{error}</p> : <p className="muted">{t("Loading…")}</p>}
      </Page>
    );
  const leased = a.lease && new Date(a.lease.expires_at) > new Date();
  const shown = version || a.version;
  const current = version === 0 || version === a.version;
  const when = (t: string) => new Date(t).toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <Page
      className="artifact-view"
      label={t("Artifact")}
      title={
        <>
          <span aria-hidden>{a.kind === "document" ? "📄 " : "🗂 "}</span>
          {a.name}
        </>
      }
      description={
        <>
          {a.kind === "document" ? t("Document · version {n}", { n: shown }) : tn(entries.length, "Shared state · {n} key", "Shared state · {n} keys")} ·{" "}
          {t("updated by {name}, {when}", { name: session.name(a.updated_by), when: when(a.updated_at) })}
          {a.channel_id && (
            <>
              {" "}
              ·{" "}
              {tx("in {channel}", {
                channel: (
                  <button className="link" onClick={() => navigate(`/c/${a.channel_id}`)}>
                    {session.channelName(a.channel_id)}
                  </button>
                ),
              })}
            </>
          )}
        </>
      }
      actions={
        <>
          {leased && <Badge tone="warn">{t("Being edited by {name}", { name: session.name(a.lease!.holder_id) })}</Badge>}
          {a.kind === "document" && draft === null && current && !leased && (
            <button className="primary" onClick={() => setDraft(a.body ?? "")}>
              {t("Edit")}
            </button>
          )}
        </>
      }
    >
      {error && <p className="error">{error}</p>}
      {a.kind === "document" ? (
        <div className="artifact-split">
          <Card className="artifact-doc">
            {!current && (
              <p className="note">
                {t("This is version {n}, not the latest.", { n: version })}{" "}
                <button className="link" onClick={() => setVersion(0)}>
                  {t("Show version {n}", { n: a.version })}
                </button>
              </p>
            )}
            {draft === null ? (
              <pre className="artifact-body">{a.body}</pre>
            ) : (
              <>
                <textarea aria-label={t("Document")} value={draft} onChange={(e) => setDraft(e.target.value)} rows={18} autoFocus />
                <div className="form-actions">
                  <button onClick={() => setDraft(null)}>{t("Cancel")}</button>
                  <button className="primary" onClick={save}>
                    {t("Save version {n}", { n: a.version + 1 })}
                  </button>
                </div>
              </>
            )}
          </Card>
          <Card title={t("Versions")} className="artifact-versions-card">
            <ul className="rows artifact-versions">
              {versions.map((v) => (
                <li key={v.version} className={v.version === shown ? "current" : ""}>
                  <button className="version-row" onClick={() => setVersion(v.version === a.version ? 0 : v.version)} aria-current={v.version === shown ? "true" : undefined}>
                    <span className="row-main">
                      <span className="row-title">{t("Version {n}", { n: v.version })}</span>
                      <span className="row-meta">
                        {session.name(v.author_id)} · {when(v.created_at)} · {formatSize(v.size)}
                      </span>
                    </span>
                    {v.version === a.version && <Badge tone="accent">{t("Latest")}</Badge>}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : (
        <Card title={t("State")} description={t("What the agents keep here, key by key. Each key has its own version.")}>
          {entries.length === 0 ? (
            <EmptyState icon="🗂" title={t("Nothing stored yet")} />
          ) : (
            <div className="table-wrap">
              <table className="artifact-kv">
                <thead>
                  <tr>
                    <th>{t("Key")}</th>
                    <th>{t("Value")}</th>
                    <th className="num">{t("Version")}</th>
                    <th>{t("Updated by")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.key}>
                      <td>
                        <strong>{e.key}</strong>
                      </td>
                      <td>
                        <code>{JSON.stringify(e.value)}</code>
                      </td>
                      <td className="num">{e.version}</td>
                      <td>{session.name(e.updated_by)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      <p className="muted artifact-ref">
        {tx("Agents refer to it as {ref}", { ref: <code>{a.ref}</code> })}
      </p>
    </Page>
  );
}

function formatSize(n: number) {
  return n < 1024 ? t("{n} bytes", { n }) : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ChannelArtifacts lists a channel's artifacts, in the bar under its
// header.
export function ChannelArtifacts({ channel }: { channel: UUID }) {
  const session = useSession();
  const state = useLantern();
  const [list, setList] = useState<Artifact[]>([]);
  const bump = state.artifactBumps.get(channel);
  useEffect(() => {
    session.api.channelArtifacts(channel).then(
      (r) => setList(r.artifacts),
      () => setList([]),
    );
  }, [session, channel, bump]);
  return (
    <>
      {list.slice(0, 8).map((a) => (
        <button
          key={a.id}
          className="artifact-chip"
          onClick={() => navigate(`/artifacts/${a.id}`)}
          title={a.kind === "document" ? t("document, version {n}", { n: a.version }) : t("kv, version {n}", { n: a.version })}
        >
          {a.kind === "document" ? "📄" : "🗂"} {a.name}
        </button>
      ))}
    </>
  );
}
